// Single-line interception point for Helius RPC tracking.
//
// `@solana/web3.js` Connection accepts a `fetch` option in its constructor.
// Every JSON-RPC HTTP request goes through that fetch — `getAccountInfo`,
// `getMultipleAccountsInfo`, `sendTransaction`, etc. By passing this
// instrumented fetch, we get a single chokepoint that observes the
// JSON-RPC method + params for every call without touching consumer code.
//
// Production: returns `undefined` so the Connection falls back to the
// platform's native `fetch`. Combined with the `process.env.NODE_ENV`
// ternary at the call site (in `tx-engine/connection.ts`), Webpack tree-
// shakes this module out of the prod bundle entirely.

import { captureCallSite, recordHeliusCall } from './helius-registry';

const IS_DEV: boolean = process.env.NODE_ENV !== 'production';

/** Truncation cap for the per-call params preview shown in the UI. */
const PARAMS_PREVIEW_MAX_CHARS = 120;

type JsonValue =
  | string
  | number
  | boolean
  | null
  | readonly JsonValue[]
  | { readonly [key: string]: JsonValue };

interface JsonRpcRequest {
  method?: string;
  params?: JsonValue;
}

interface RpcMeta {
  method: string;
  paramsKey: string;
  paramsPreview: string;
  bytes: number;
}

function safeStringify(value: JsonValue): string {
  try {
    return JSON.stringify(value);
  } catch {
    // SILENT-CATCH: circular params or unserialisable values — fall back
    // to the typeof so the dedup key stays a primitive string.
    return `<unserialisable:${typeof value}>`;
  }
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

function extractRpcMeta(body: BodyInit | null | undefined): RpcMeta | RpcMeta[] | null {
  if (typeof body !== 'string') return null;
  const bytes = body.length;
  let parsed: JsonRpcRequest | JsonRpcRequest[];
  try {
    parsed = JSON.parse(body) as JsonRpcRequest | JsonRpcRequest[];
  } catch {
    // SILENT-CATCH: not a JSON-RPC body — skip recording, still proxy.
    return null;
  }
  if (Array.isArray(parsed)) {
    // Batched request — emit one record per sub-call so the ledger
    // shows what was actually inside the batch. Bytes are split evenly
    // across the entries so the totals still sum to the wire size.
    const batchBytes = Math.max(1, Math.floor(bytes / parsed.length));
    const out: RpcMeta[] = [];
    for (const sub of parsed) {
      if (typeof sub.method !== 'string') continue;
      const paramsKey = safeStringify(sub.params ?? []);
      out.push({
        method: `batch:${sub.method}`,
        paramsKey,
        paramsPreview: truncate(paramsKey, PARAMS_PREVIEW_MAX_CHARS),
        bytes: batchBytes,
      });
    }
    return out;
  }
  const single: JsonRpcRequest = parsed;
  if (typeof single.method !== 'string') return null;
  const paramsKey = safeStringify(single.params ?? []);
  return {
    method: single.method,
    paramsKey,
    paramsPreview: truncate(paramsKey, PARAMS_PREVIEW_MAX_CHARS),
    bytes,
  };
}

/**
 * Returns a `fetch` implementation that records every Helius RPC call —
 * with both request meta and the response body so the DevToolkit can
 * show the full payload on click. Returns `undefined` in production so
 * the Connection uses native fetch unchanged.
 */
export function makeInstrumentedHeliusFetch(): typeof fetch | undefined {
  if (!IS_DEV) return undefined;
  return async function instrumentedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const meta = init?.body !== undefined && init.body !== null ? extractRpcMeta(init.body) : null;
    const callSite = meta !== null ? captureCallSite() : null;

    const response = await fetch(input, init);

    if (meta !== null) {
      // Read the response body without disturbing the caller. `clone()`
      // gives us our own readable stream; the caller's response is
      // still pristine. Errors reading the clone are non-fatal.
      let responseBody: string | null = null;
      try {
        responseBody = await response.clone().text();
      } catch {
        // SILENT-CATCH: stream read failed — record without body
        responseBody = null;
      }

      if (Array.isArray(meta)) {
        // Batched JSON-RPC: every sub-record gets the same response
        // body since it's a single HTTP envelope.
        for (const m of meta) {
          recordHeliusCall({
            method: m.method,
            paramsPreview: m.paramsPreview,
            paramsKey: m.paramsKey,
            bytes: m.bytes,
            callSite,
            responseBody,
          });
        }
      } else {
        recordHeliusCall({
          method: meta.method,
          paramsPreview: meta.paramsPreview,
          paramsKey: meta.paramsKey,
          bytes: meta.bytes,
          callSite,
          responseBody,
        });
      }
    }

    return response;
  };
}
